import { useState, useEffect, useCallback, useMemo } from "react";
import { ApolloError } from "@apollo/client";
import {
  DataGrid,
  GridToolbar,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { useAuth } from "../../context/AuthContext";
import { useGetWorkspaceUsersByWorkspaceIdQuery } from "../../graphql/queries/getWorkspaceUsersByWorkspaceId.generated";
import { useGetWorkspacesQuery } from "../../graphql/queries/getWorkspaces.generated";
import { useUpdateUserMutation } from "../../graphql/mutations/updateUser.generated";

import Colors from "../../styles/colors";

const ManageUsers = () => {
  const { loggedUserData } = useAuth();
  const [workspaceIds, setWorkspaceIds] = useState<Array<number>>([]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      id: false,
      workspace: false,
    });

  const handleOnCompletedGetWorkspaces = useCallback((data: any) => {
    console.log("Get Workspaces data: ", data);
  }, []);

  const handleOnErrorsGetWorkspaces = useCallback((error: ApolloError) => {
    console.log("Get Workspaces error: ", error);
  }, []);

  const { data: workspacesData, loading: workspacesDataLoading } =
    useGetWorkspacesQuery({
      skip: !loggedUserData?.isHighnoteAdmin,
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedGetWorkspaces,
      onError: handleOnErrorsGetWorkspaces,
    });

  const handleOnCompletedGetWorkspaceUsersByWorkspaceId = useCallback(
    (data: any) => {
      console.log("Get WorkspaceUsers By Workspace Id data: ", data);
    },
    []
  );

  const handleOnErrorsGetWorkspaceUsersByWorkspaceId = useCallback(
    (error: ApolloError) => {
      console.log("Get WorkspaceUsers By Workspace Id error: ", error);
    },
    []
  );

  const {
    data: usersData,
    loading: usersDataLoading,
    refetch,
  } = useGetWorkspaceUsersByWorkspaceIdQuery({
    variables: {
      workspaceIds: workspaceIds,
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onCompleted: handleOnCompletedGetWorkspaceUsersByWorkspaceId,
    onError: handleOnErrorsGetWorkspaceUsersByWorkspaceId,
  });

  useEffect(() => {
    console.log("ManageUsers loggedUserData: ", loggedUserData);

    if (!loggedUserData) {
      setWorkspaceIds([]);
      return;
    }

    // If is Highnote Admin, use all available workspaces
    if (loggedUserData.isHighnoteAdmin && workspacesData?.workspaces?.length) {
      setWorkspaceIds(
        workspacesData.workspaces.map((workspace) => workspace.id)
      );
      return;
    }

    // If the user is Workspace Admin, use only their workspace
    if (
      loggedUserData.isWorkspaceAdmin &&
      loggedUserData.workspaces[0]?.workspaceId
    ) {
      setWorkspaceIds([loggedUserData.workspaces[0].workspaceId]);
      return;
    }

    // In any other case, do not show users
    setWorkspaceIds([]);
  }, [loggedUserData, workspacesData]);

  const handleOnCompletedUpdateUser = useCallback(
    (data: { update_users_by_pk: any }) => {
      console.log("Update User Completed", data.update_users_by_pk);
      refetch();
    },
    [refetch]
  );

  const [updateUser] = useUpdateUserMutation({
    errorPolicy: "none",
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: (error) => console.log(error),
    onCompleted: (data) => handleOnCompletedUpdateUser,
  });

  const handleOnToggleAccountEnabled = useCallback(
    async (uid: string, isEnabled: boolean) => {
      console.log("Toggle Account Enabled ", isEnabled);
      const updatedUser = {
        isEnabled: !isEnabled,
      };

      await updateUser({
        variables: {
          userId: uid,
          objects: updatedUser,
        },
      });
    },
    []
  );

  const handleOnToggleMentorEnabled = useCallback(
    async (userId: string, mentorEnabled: boolean) => {
      const updatedUser = {
        mentorEnabled: !mentorEnabled,
      };

      await updateUser({
        variables: {
          userId: userId,
          objects: updatedUser,
        },
      });
    },
    [updateUser]
  );

  const handleOnToggleMenteeEnabled = useCallback(
    (userId: string, menteeEnabled: boolean) => {
      const updatedUser = {
        menteeEnabled: !menteeEnabled,
      };

      updateUser({
        variables: {
          userId: userId,
          objects: updatedUser,
        },
      });
    },
    [updateUser]
  );

  const handleOnToggleIsContributor = useCallback(
    (userId: string, isContributor: boolean) => {
      const updatedUser = {
        isContributor: !isContributor,
      };

      updateUser({
        variables: {
          userId: userId,
          objects: updatedUser,
        },
      });
    },
    [updateUser]
  );

  const rows = useMemo(() => {
    if (usersData?.workspaceUsers.length) {
      const formattedUsers = usersData?.workspaceUsers.map((element: any) => {
        const {
          id,
          firstName,
          lastName,
          role,
          userSpecialty,
          workspaces,
          workspaceLocation,
          mentorEnabled,
          menteeEnabled,
          isContributor,
          isEnabled,
        } = element.user;

        const workspaceName =
          workspaces && workspaces[0] && workspaces[0].workspace.name
            ? workspaces[0].workspace.name
            : "";

        const workspaceLocationName =
          workspaceLocation && workspaceLocation.name
            ? workspaceLocation.name
            : "";

        const roleName = role && role.name ? role.name : "";

        const specialtyName =
          userSpecialty && userSpecialty.name ? userSpecialty.name : "";

        return {
          id,
          firstName,
          lastName,
          workspace: workspaceName,
          workspaceLocation: workspaceLocationName,
          role: roleName,
          specialty: specialtyName,
          mentorEnabled,
          menteeEnabled,
          isContributor,
          isEnabled,
        };
      });

      return formattedUsers;
    }

    return [];
  }, [usersData?.workspaceUsers]);

  const columns = useMemo(() => {
    return [
      { field: "id", headerName: "ID", flex: 0.5, minWidth: 100 },
      {
        field: "firstName",
        headerName: "First Name",
        flex: 0.5,
        minWidth: 130,
      },
      { field: "lastName", headerName: "Last Name", flex: 1, minWidth: 130 },
      { field: "workspace", headerName: "Workspace", flex: 1, minWidth: 130 },
      {
        field: "workspaceLocation",
        headerName: "Location",
        flex: 1,
        minWidth: 150,
      },
      { field: "role", headerName: "Role", flex: 1, minWidth: 150 },
      { field: "specialty", headerName: "Specialty", flex: 1, minWidth: 150 },
      {
        field: "mentorEnabled",
        headerName: "Mentor Enabled",
        flex: 0.5,
        minWidth: 100,
        renderCell: (params: any) => (
          <button
            onClick={() =>
              handleOnToggleMentorEnabled(params.row.id, params.value)
            }
            style={{
              backgroundColor: params.value ? "green" : "red",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "5px 10px",
              cursor: "pointer",
            }}
          >
            {params.value ? "Enabled" : "Disabled"}
          </button>
        ),
      },
      {
        field: "menteeEnabled",
        headerName: "Mentee Enabled",
        flex: 0.5,
        minWidth: 100,
        renderCell: (params: any) => (
          <button
            onClick={() =>
              handleOnToggleMenteeEnabled(params.row.id, params.value)
            }
            style={{
              backgroundColor: params.value ? "green" : "red",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "5px 10px",
              cursor: "pointer",
            }}
          >
            {params.value ? "Enabled" : "Disabled"}
          </button>
        ),
      },
      {
        field: "isContributor",
        headerName: "Contributor",
        flex: 0.5,
        minWidth: 100,
        renderCell: (params: any) => (
          <button
            onClick={() =>
              handleOnToggleIsContributor(params.row.id, params.value)
            }
            style={{
              backgroundColor: params.value ? "green" : "red",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "5px 10px",
              cursor: "pointer",
            }}
          >
            {params.value ? "Yes" : "No"}
          </button>
        ),
      },
      {
        field: "isEnabled",
        headerName: "Enabled",
        flex: 0.5,
        minWidth: 100,
        renderCell: (params: any) => (
          <button
            onClick={() => {
              handleOnToggleAccountEnabled(params.row.id, params.value);
            }}
            style={{
              backgroundColor: params.value ? "green" : "red",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "5px 10px",
              cursor: "pointer",
            }}
          >
            {params.value ? "Enabled" : "Disabled"}
          </button>
        ),
      },
    ];
  }, [
    handleOnToggleMentorEnabled,
    handleOnToggleMenteeEnabled,
    handleOnToggleIsContributor,
  ]);

  return (
    <Container component="main" maxWidth={false}>
      <h1 className="screen-title">Manage Users</h1>

      {usersDataLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : usersData?.workspaceUsers.length ? (
        <DataGrid
          rows={rows}
          columns={columns}
          disableMultipleRowSelection={false}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            filterPanel: {
              disableAddFilterButton: false,
              disableRemoveAllButton: false,
            },
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: Colors.Highnote.blue4,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: Colors.Highnote.white,
              fontWeight: "900",
            },
            "& .MuiDataGrid-cell": {
              border: 0.5,
              borderColor: Colors.Highnote.blue1,
              color: Colors.Highnote.black,
              fontWeight: "bold",
            },
            "& .MuiDataGrid-row:hover": {
              border: 1,
              borderColor: Colors.Highnote.blue2,
              color: "primary.main",
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: Colors.Highnote.blue4,
              color: Colors.Highnote.white,
              fontWeight: "900",
            },
          }}
        />
      ) : (
        <h3>No users to display.</h3>
      )}
    </Container>
  );
};

export default ManageUsers;
